/**
 * Fleet trades blotter — operator view, all trades across clients/accounts/
 * strategies, fully filterable. Backed by /admin/trades.
 *
 * Designed for extensibility:
 *   - every filter is OPTIONAL — pass none for "all", add filters incrementally.
 *   - extra response fields are tolerated (Zod parses what's declared, ignores
 *     the rest — older FE keeps working when BE adds new denormalized fields).
 *   - new filters (broker_type, side, sizing, …) slot in by adding a query
 *     param + an optional dropdown; no schema rewrite.
 */
import { z } from 'zod';

import type { ApiResult } from './client';
import { get } from './client';

export const adminTradeSchema = z.object({
  id: z.string(),
  symbol: z.string(),
  side: z.string(),
  quantity: z.number(),
  entry_price: z.number(),
  exit_price: z.number().nullable(),
  realized_pnl: z.number(),
  charges: z.number(),
  is_open: z.boolean(),
  entry_time: z.string().nullable(),
  exit_time: z.string().nullable(),
  entry_signal: z.string().nullable(),
  exit_signal: z.string().nullable(),
  // Denormalized ownership
  client_id: z.string(),
  client_email: z.string().nullable(),
  client_display_name: z.string().nullable(),
  broker_account_id: z.string(),
  broker_account_display_name: z.string().nullable(),
  broker_type: z.string().nullable(),
  subscription_id: z.string().nullable(),
  strategy_id: z.string().nullable(),
});
export const adminTradeListSchema = z.array(adminTradeSchema);
export type AdminTrade = z.infer<typeof adminTradeSchema>;

export interface ListAdminTradesQuery {
  client_id?: string;
  broker_account_id?: string;
  subscription_id?: string;
  strategy_id?: string;
  symbol?: string;
  is_open?: boolean;
  since?: string;
  until?: string;
  limit?: number;
  offset?: number;
}

export const listAdminTrades = (
  query: ListAdminTradesQuery = {},
  signal?: AbortSignal,
): Promise<ApiResult<AdminTrade[]>> =>
  get('/admin/trades', adminTradeListSchema, {
    signal,
    query: {
      client_id: query.client_id,
      broker_account_id: query.broker_account_id,
      subscription_id: query.subscription_id,
      strategy_id: query.strategy_id,
      symbol: query.symbol,
      is_open: query.is_open,
      since: query.since,
      until: query.until,
      limit: query.limit,
      offset: query.offset,
    },
  }) as Promise<ApiResult<AdminTrade[]>>;
